import { useState, FormEvent } from 'react';
import Layout from '@/components/feature/Layout';
import SEO from '@/components/base/SEO';
import Breadcrumb from '@/components/base/Breadcrumb';

const aboutSchema = {
  '@context': 'https://schema.org',
  '@type': 'AboutPage',
  name: '关于新有菜 - 新鲜蔬菜直供平台',
  description: '新有菜品牌介绍，了解我们的使命愿景、品质承诺、口碑信誉及联系方式。',
  url: 'https://www.xinyoucai.com/about',
};

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt');
    if (honeypot && String(honeypot).trim() !== '') {
      setFormStatus('success');
      form.reset();
      setTimeout(() => setFormStatus('idle'), 3000);
      return;
    }

    setFormStatus('submitting');
    setFormError('');

    try {
      const params = new URLSearchParams();
      formData.forEach((value, key) => {
        if (key !== 'phone_alt' || !String(value).trim()) {
          params.append(key, String(value));
        }
      });

      const response = await fetch(form.action, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try { parsed = JSON.parse(responseText); } catch { parsed = null; }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
        setTimeout(() => setFormStatus('idle'), 4000);
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (String(serverMsg).toLowerCase().includes('spam')) {
          setFormStatus('success');
          form.reset();
          setTimeout(() => setFormStatus('idle'), 3000);
        } else {
          setFormError(String(serverMsg));
          setFormStatus('error');
        }
      }
    } catch {
      setFormError('网络错误，请检查网络后重试');
      setFormStatus('error');
    }
  };

  return (
    <Layout>
      <SEO
        title="关于我们 - 新有菜 | 品牌介绍、口碑信誉与联系方式"
        description="了解新有菜品牌故事与使命，全国500+优质产地直供，每日新鲜蔬菜冷链配送，品质保障、口碑见证。联系我们获取更多服务信息。"
        keywords="新有菜品牌,蔬菜直供平台,关于我们,联系我们,品牌介绍"
        canonicalUrl="/about"
        schema={[aboutSchema]}
      />

      <div className="pt-20 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', href: '/' },
              { label: '关于我们' },
            ]}
          />

          {/* Brand Introduction */}
          <section className="mb-16" aria-labelledby="brand-heading">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
              <div>
                <span className="inline-block px-3 py-1 bg-primary-50 text-primary-700 text-xs font-medium rounded-full mb-4">
                  品牌介绍
                </span>
                <h2 id="brand-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-5">
                  从田间到餐桌，<br />我们重新定义蔬菜的新鲜
                </h2>
                <div className="space-y-4 text-sm text-foreground-600 leading-relaxed">
                  <p>
                    <strong className="text-foreground-900">新有菜</strong>成立于2020年，是一家专注于新鲜蔬菜直供的互联网平台。我们致力于打破传统蔬菜供应链的层层壁垒，将田间最新鲜的蔬菜直接送达千家万户。
                  </p>
                  <p>
                    我们深入全国优质蔬菜产区，与<strong className="text-foreground-900">500多个蔬菜种植基地</strong>建立了长期稳定的合作关系。从山东寿光的叶菜、河北张家口的根茎类，到云南昆明的豆类、浙江庆元的菌菇——新有菜的供应链覆盖了中国最优质的蔬菜产地。
                  </p>
                  <p>
                    每一份蔬菜从采摘到送达，全程不超过<strong className="text-foreground-900">24小时</strong>。我们建立了严格的品控体系：凌晨采摘、清晨分拣、专业包装、冷链配送——确保每一片叶子都保持采摘时的新鲜状态。
                  </p>
                </div>
              </div>
              <div className="aspect-[4/3] rounded-xl overflow-hidden">
                <img
                  src="https://readdy.ai/api/search-image?query=Modern%20vegetable%20distribution%20center%20with%20fresh%20organic%20produce%20being%20sorted%20and%20packed%20by%20workers%20in%20clean%20uniforms%2C%20bright%20natural%20lighting%2C%20professional%20food%20industry%20setting%2C%20clean%20minimal%20aesthetic&width=900&height=675&seq=about-brand-01&orientation=landscape"
                  alt="新有菜品牌 - 新鲜蔬菜分拣配送中心"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </section>

          {/* Mission Values */}
          <section className="mb-16" aria-labelledby="mission-heading">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {[
                {
                  icon: 'ri-eye-line',
                  title: '我们的愿景',
                  desc: '成为中国最值得信赖的新鲜蔬菜直供平台，让每个家庭都能轻松获取优质新鲜蔬菜。',
                },
                {
                  icon: 'ri-flag-line',
                  title: '我们的使命',
                  desc: '连接优质产地与千家万户，缩短蔬菜从田间到餐桌的距离，推动健康饮食文化。',
                },
                {
                  icon: 'ri-heart-line',
                  title: '我们的价值观',
                  desc: '品质第一、客户至上、诚信经营、持续创新。用心做好每一份蔬菜，不辜负每一份信任。',
                },
              ].map((item, index) => (
                <div
                  key={item.title}
                  className="p-6 bg-background-100 rounded-xl text-center"
                  style={{ animation: `slideUp 400ms ease-out ${index * 100}ms both` }}
                >
                  <div className="w-12 h-12 mx-auto mb-4 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
                    <i className={`${item.icon} text-xl`}></i>
                  </div>
                  <h3 className="text-base font-semibold text-foreground-900 mb-2">{item.title}</h3>
                  <p className="text-sm text-foreground-500 leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Reputation Section */}
          <section className="mb-16" aria-labelledby="reputation-heading">
            <span className="inline-block px-3 py-1 bg-accent-50 text-accent-700 text-xs font-medium rounded-full mb-4">
              口碑信誉
            </span>
            <h2 id="reputation-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              50万+家庭的信赖之选
            </h2>
            <p className="text-sm text-foreground-500 mb-8 max-w-2xl">
              自成立以来，新有菜已服务超过50万个家庭和2000多家餐饮企业，用品质赢得口碑，用服务收获信赖。
            </p>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 md:gap-5 mb-10">
              {[
                { value: '50万+', label: '服务家庭', icon: 'ri-home-heart-line' },
                { value: '2000+', label: '合作餐饮企业', icon: 'ri-restaurant-line' },
                { value: '500+', label: '直供蔬菜基地', icon: 'ri-plant-line' },
                { value: '99.7%', label: '客户好评率', icon: 'ri-thumb-up-line' },
              ].map((stat, index) => (
                <div
                  key={stat.label}
                  className="p-5 bg-background-100 rounded-xl text-center"
                  style={{ animation: `slideUp 400ms ease-out ${index * 80}ms both` }}
                >
                  <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center rounded-full bg-accent-100 text-accent-600">
                    <i className={`${stat.icon} text-lg`}></i>
                  </div>
                  <p className="text-2xl font-heading font-bold text-accent-600 mb-1">{stat.value}</p>
                  <p className="text-xs text-foreground-500">{stat.label}</p>
                </div>
              ))}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                {
                  name: '张女士',
                  role: '家庭用户 · 北京',
                  comment: '自从用了新有菜，家里的蔬菜品质明显提升了。每天早上收到的菜都特别新鲜，孩子也爱上了吃蔬菜。配送也很准时，非常方便！',
                  rating: 5,
                },
                {
                  name: '李经理',
                  role: '餐饮企业 · 上海',
                  comment: '作为餐厅采购负责人，新有菜的品质和服务让我很放心。蔬菜新鲜度高、品类齐全，价格也很合理。合作两年了，从未让我失望。',
                  rating: 5,
                },
                {
                  name: '王阿姨',
                  role: '家庭用户 · 广州',
                  comment: '退休之后对吃的越来越讲究了，新有菜的有机蔬菜特别合我心意。虽然价格稍高一些，但品质真的不一样，吃得安心最重要。',
                  rating: 5,
                },
              ].map((review, index) => (
                <div
                  key={review.name}
                  className="p-5 bg-background-50 rounded-xl border border-background-200/70"
                  style={{ animation: `slideUp 400ms ease-out ${index * 100}ms both` }}
                >
                  <div className="flex items-center gap-1 mb-3">
                    {Array.from({ length: review.rating }).map((_, i) => (
                      <i key={i} className="ri-star-fill text-accent-500 text-sm"></i>
                    ))}
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4">{review.comment}</p>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center">
                      <span className="text-xs font-semibold text-secondary-700">{review.name[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-800">{review.name}</p>
                      <p className="text-xs text-foreground-400">{review.role}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* Contact Section */}
          <section aria-labelledby="contact-heading">
            <span className="inline-block px-3 py-1 bg-secondary-50 text-secondary-700 text-xs font-medium rounded-full mb-4">
              联系我们
            </span>
            <h2 id="contact-heading" className="text-2xl md:text-3xl font-heading font-bold text-foreground-950 mb-3">
              期待与您沟通
            </h2>
            <p className="text-sm text-foreground-500 mb-8 max-w-2xl">
              无论您是想咨询蔬菜订购、企业合作，还是有任何建议和意见，我们都非常欢迎您的来信。
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              <div className="space-y-5">
                {[
                  { icon: 'ri-phone-line', label: '客服热线', value: '400-123-4567', sub: '工作日 8:00-20:00' },
                  { icon: 'ri-mail-line', label: '电子邮箱', value: 'hello@xinyoucai.com', sub: '我们将在24小时内回复' },
                  { icon: 'ri-map-pin-line', label: '公司地址', value: '北京市朝阳区望京科技园A座1508室', sub: '' },
                ].map((item) => (
                  <div key={item.label} className="flex items-start gap-4 p-4 bg-background-100 rounded-xl">
                    <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-primary-100 text-primary-600 shrink-0">
                      <i className={`${item.icon} text-lg`}></i>
                    </div>
                    <div>
                      <p className="text-xs text-foreground-400 mb-0.5">{item.label}</p>
                      <p className="text-sm font-medium text-foreground-900">{item.value}</p>
                      {item.sub && <p className="text-xs text-foreground-400 mt-0.5">{item.sub}</p>}
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-background-100 rounded-xl p-5 md:p-6">
                <h3 className="text-lg font-semibold text-foreground-900 mb-4">发送消息</h3>
                <form
                  data-readdy-form="true"
                  id="about-contact-form"
                  action="https://readdy.ai/api/form/d9th10l3pcjqs2fcse4g"
                  method="POST"
                  encType="application/x-www-form-urlencoded"
                  className="space-y-4"
                  onSubmit={handleSubmit}
                >
                  <div>
                    <label htmlFor="contact-name" className="block text-sm text-foreground-700 mb-1.5">姓名</label>
                    <input
                      type="text"
                      id="contact-name"
                      name="name"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的姓名"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-email" className="block text-sm text-foreground-700 mb-1.5">邮箱</label>
                    <input
                      type="email"
                      id="contact-email"
                      name="email"
                      required
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的邮箱"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-phone" className="block text-sm text-foreground-700 mb-1.5">电话</label>
                    <input
                      type="tel"
                      id="contact-phone"
                      name="phone"
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors"
                      placeholder="请输入您的电话（选填）"
                    />
                  </div>
                  <div>
                    <label htmlFor="contact-message" className="block text-sm text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="contact-message"
                      name="message"
                      required
                      rows={4}
                      maxLength={500}
                      className="w-full px-3 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-300 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-200 transition-colors resize-none"
                      placeholder="请输入您的留言内容（最多500字）"
                    ></textarea>
                    <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
                  </div>
                  <input
                    type="text"
                    name="phone_alt"
                    className="website-alt-field"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />
                  <button
                    type="submit"
                    disabled={formStatus === 'submitting'}
                    className="w-full px-5 py-3 bg-primary-500 text-background-50 font-medium rounded-lg text-sm hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {formStatus === 'submitting' ? '发送中...' : '发送消息'}
                  </button>
                  {formStatus === 'success' && (
                    <p className="text-sm text-primary-600 text-center mt-2">消息已成功发送，我们会尽快回复您！</p>
                  )}
                  {formStatus === 'error' && (
                    <p className="text-sm text-red-500 text-center mt-2">{formError}</p>
                  )}
                </form>
              </div>
            </div>
          </section>
        </div>
      </div>
    </Layout>
  );
}